import {
  CustomFieldTypeDto,
  FIBONACCI_SEQUENCE,
  type FibonacciFieldConfigurationDto,
  type FieldConfigurationDto,
} from '@tempo-io/fim3-dto';
import { format, isAfter, isBefore, isValid, parse } from 'date-fns';
import type { TFunction } from 'i18next';
import {
  getCustomFieldDateDisplay,
  isSystemField,
  removeSystemFieldNamePrefix,
} from '../../utils/Utilities';
import { getFormulaDataString } from './CustomFieldValue';

// Default limits of DatePicker https://mui.com/x/react-date-pickers/validation/
const MinDate = new Date(1900, 0, 1);
const MaxDate = new Date(2099, 11, 31);

const isNil = (value: unknown): value is null | undefined => {
  return value === null || value === undefined;
};

const isNullOrWhitespace = (input: string | undefined | null) => {
  return !input || !input.trim();
};

const isEmptyValues = (fieldConfiguration: FieldConfigurationDto, values: string[]): boolean => {
  if (fieldConfiguration.fieldType === CustomFieldTypeDto.DATE_RANGE) {
    return isNullOrWhitespace(values[0]) || isNullOrWhitespace(values[1]);
  }
  // All other types just have 1 value
  return isNullOrWhitespace(values[0]);
};

export const validateField = (
  name: string,
  fieldConfiguration: FieldConfigurationDto,
  required: boolean | undefined,
  values: string[],
  t: TFunction,
): string | undefined => {
  let errorMessage: string | undefined;
  name = isSystemField(name) ? removeSystemFieldNamePrefix(name) : name;

  if (required && isEmptyValues(fieldConfiguration, values)) {
    errorMessage = t(($) => $.ideaManagement.general.pleaseFillOutThisField, { name });
  } else if (fieldConfiguration.fieldType === CustomFieldTypeDto.TEXT && !!values[0]) {
    if (!isNil(fieldConfiguration.maxLength) && values[0].length > fieldConfiguration.maxLength) {
      errorMessage = t(($) => $.ideaManagement.general.formValidationStringFieldIsTooLong, {
        max: fieldConfiguration.maxLength,
      });
    }

    const validationRegex = fieldConfiguration.validationRegex;
    if (validationRegex) {
      const regex = new RegExp(validationRegex);
      const hasError = !regex.test(values[0]);
      if (hasError) {
        errorMessage = t(($) => $.ideaManagement.general.stringDoesNotMatchValidationExpression);
      }
    }
  } else if (
    fieldConfiguration.fieldType === CustomFieldTypeDto.NUMBER ||
    fieldConfiguration.fieldType === CustomFieldTypeDto.CURRENCY ||
    fieldConfiguration.fieldType === CustomFieldTypeDto.PERCENT
  ) {
    if (
      !isNil(fieldConfiguration.minimum) &&
      parseFloat(values[0] as string) < fieldConfiguration.minimum
    ) {
      errorMessage = t(($) => $.ideaManagement.general.valueMustBeGreaterThan, {
        value: fieldConfiguration.minimum,
      });
    }

    if (
      !isNil(fieldConfiguration.maximum) &&
      parseFloat(values[0] as string) > fieldConfiguration.maximum
    ) {
      errorMessage = t(($) => $.ideaManagement.general.valueMustBeLessThan, {
        value: fieldConfiguration.maximum,
      });
    }

    // Cannot just be checking truthiness because 0 isn't truthy.
    if (!isNil(fieldConfiguration.places) && values[0]) {
      const decimalIndex = values[0].toString().indexOf('.');
      const places = values[0].length - decimalIndex - 1;
      if (
        decimalIndex > 0 &&
        places > fieldConfiguration.places &&
        !(fieldConfiguration.places < 1 && decimalIndex === -1)
      ) {
        errorMessage = t(($) => $.ideaManagement.general.valueMustHaveAtMostNDecimalPlaces, {
          places: Math.max(fieldConfiguration.places, 0),
        });
      }
    }
  } else if (fieldConfiguration.fieldType === CustomFieldTypeDto.FORMULA) {
    const { value, hasError } = getFormulaDataString(t, values);
    if (hasError) {
      errorMessage = value;
    }
  } else if (fieldConfiguration.fieldType === CustomFieldTypeDto.RATING) {
    // Valid ratings: 0 to 5 inclusive
    if (!isNil(values[0]) && values[0].length > 0) {
      const intVal = parseInt(values[0]);
      if (Number.isNaN(intVal) || intVal < 0 || intVal > 5) {
        errorMessage = t(($) => $.ideaManagement.general.valueMustBeBetweenZeroAndFive);
      }
    }
  } else if (fieldConfiguration.fieldType === CustomFieldTypeDto.FIBONACCI) {
    const value = values[0];
    if (!isNullOrWhitespace(value) && value !== undefined) {
      const intVal = parseInt(value, 10);
      const fibConfig = fieldConfiguration as FibonacciFieldConfigurationDto;
      const validFibNumbers = FIBONACCI_SEQUENCE.filter((f) => f <= fibConfig.maxValue);

      if (Number.isNaN(intVal) || !validFibNumbers.includes(intVal as typeof FIBONACCI_SEQUENCE[number])) {
        errorMessage = t(($) => $.ideaManagement.general.valueMustBeValidFibonacci);
      }
    }
  } else if (fieldConfiguration.fieldType === CustomFieldTypeDto.DATE) {
    if (!isNullOrWhitespace(values[0])) {
      const parsed = parse(values[0] ?? '', 'yyyy-MM-dd', new Date());
      if (!isValid(parsed) || format(parsed, 'yyyy-MM-dd') !== values[0]) {
        errorMessage = t(($) => $.ideaManagement.general.dateIsInvalid);
      } else if (isBefore(parsed, MinDate) || isAfter(parsed, MaxDate)) {
        errorMessage = t(($) => $.ideaManagement.general.datesMustBeBetween, {
          minDate: getCustomFieldDateDisplay(MinDate),
          maxDate: getCustomFieldDateDisplay(MaxDate),
        });
      }
    }
  } else if (fieldConfiguration.fieldType === CustomFieldTypeDto.DATE_RANGE) {
    if (!(isNullOrWhitespace(values[0]) && isNullOrWhitespace(values[1]))) {
      const startDate = parse(values[0] ?? '', 'yyyy-MM-dd', new Date());
      const endDate = parse(values[1] ?? '', 'yyyy-MM-dd', new Date());
      if (!isValid(startDate) || format(startDate, 'yyyy-MM-dd') !== values[0]) {
        errorMessage = t(($) => $.ideaManagement.general.startDateIsInvalid);
      } else if (!isValid(endDate) || format(endDate, 'yyyy-MM-dd') !== values[1]) {
        errorMessage = t(($) => $.ideaManagement.general.endDateIsInvalid);
      } else if (
        isBefore(startDate, MinDate) ||
        isAfter(startDate, MaxDate) ||
        isBefore(endDate, MinDate) ||
        isAfter(endDate, MaxDate)
      ) {
        errorMessage = t(($) => $.ideaManagement.general.datesMustBeBetween, {
          minDate: getCustomFieldDateDisplay(MinDate),
          maxDate: getCustomFieldDateDisplay(MaxDate),
        });
      } else if (isBefore(endDate, startDate)) {
        errorMessage = t(($) => $.ideaManagement.general.endDateCanNotBeBeforeStartDate);
      }
    }
  }
  return errorMessage;
};
